// src/pages/ChatBot/query-logs/CustomAnswerModal.tsx
//
// "Custom Answer" workbench: when the chatbot answered a query badly, an
// admin writes the answer it should have given, optionally attaching
// citations — highlighted PDF passages from the corpus browser, or retrieved
// chunks from the query's trace. Saved answers are served verbatim to
// near-duplicate questions (cosine ≥ 0.9). Uses the house smrtdocs modal
// shell at full-workbench size (AdminSMRTDocsView precedent).

import { useCallback, useEffect, useRef, useState } from 'react';
import { useEscapeClose } from '../../../hooks/useEscapeClose';
import { useModalBackdropClose } from '../../../hooks/useModalBackdropClose';
import {
  createCustomQueryLog,
  deleteCustomAnswer,
  fetchQueryLog,
  QueryLogApiError,
  saveCustomAnswer,
} from './api';
import { formatDateTime } from './derive';
import type { CustomAnswerCitation, QueryLogDetailResponse, QueryLogDoc } from './types';
import CorpusBrowser from './corpus-browser';
import './CustomAnswerModal.css';

// Two modes share this shell. EDIT (queryId set, createOpen falsy): the
// established workbench — fetch the log, edit/save/remove its custom answer.
// CREATE (createOpen true, queryId null): author a Q&A from scratch — no
// detail fetch, the question becomes an editable input, there are no retrieved
// chunks and no Remove, and Save POSTs a brand-new admin-created log. The
// panel never sets both, so createOpen alone selects the mode.
interface CustomAnswerModalProps {
  queryId: string | null;
  createOpen?: boolean;
  // Create mode only: when true, the new Q&A is created directly in the golden
  // set (passed through to createCustomQueryLog). Ignored in edit mode.
  createInEvalSet?: boolean;
  onClose: () => void;
  onChanged: () => void;
}

const QUOTE_PREVIEW_MAX_CHARS = 80;
const CHUNK_ID_PREVIEW_CHARS = 8;

function truncateQuote(text: string): string {
  if (text.length <= QUOTE_PREVIEW_MAX_CHARS) return text;
  return `${text.slice(0, QUOTE_PREVIEW_MAX_CHARS - 1).trimEnd()}…`;
}

// Selection citations dedupe on documentName+pageNumber+quotedText (the same
// phrase can legitimately be cited from different pages); chunk citations
// dedupe on chunkId.
function isDuplicateCitation(
  existing: CustomAnswerCitation[],
  candidate: CustomAnswerCitation,
): boolean {
  if (candidate.chunkId) {
    return existing.some((citation) => citation.chunkId === candidate.chunkId);
  }
  return existing.some(
    (citation) =>
      !citation.chunkId &&
      citation.documentName === candidate.documentName &&
      citation.pageNumber === candidate.pageNumber &&
      citation.quotedText === candidate.quotedText,
  );
}

function citationKey(citation: CustomAnswerCitation): string {
  return citation.chunkId
    ? `chunk:${citation.chunkId}`
    : `selection:${citation.documentName}::${citation.pageNumber ?? ''}::${citation.quotedText ?? ''}`;
}

function describeCitation(citation: CustomAnswerCitation): string {
  const reference = citation.chunkId
    ? `chunk ${citation.chunkId.slice(0, CHUNK_ID_PREVIEW_CHARS)}…`
    : `"${truncateQuote(citation.quotedText ?? '')}"`;
  return citation.pageNumber !== undefined
    ? `${reference} · page ${citation.pageNumber}`
    : reference;
}

function TopStrip({ log }: { log: QueryLogDoc }) {
  const showStandalone = Boolean(log.standaloneQuery) && log.standaloneQuery !== log.question;
  return (
    <div className="qlc-topstrip">
      <p className="qlc-question">{log.question}</p>
      {showStandalone && <p className="qlc-standalone">Standalone: {log.standaloneQuery}</p>}
      <p className="qlc-meta">
        {log.askedBy} · {formatDateTime(log.askedAt)}
      </p>
    </div>
  );
}

function CitationList({
  citations,
  onRemove,
}: {
  citations: CustomAnswerCitation[];
  onRemove: (index: number) => void;
}) {
  if (citations.length === 0) {
    return (
      <p className="qlc-empty-note">
        No citations — highlight a passage in a document or cite a retrieved chunk.
      </p>
    );
  }
  return (
    <div className="qlc-citation-list">
      {citations.map((citation, index) => (
        <div key={citationKey(citation)} className="qlc-citation-card">
          <div className="qlc-citation-text">
            <span className="qlc-citation-doc" title={citation.documentName}>
              {citation.documentName}
            </span>
            <span className="qlc-citation-meta">{describeCitation(citation)}</span>
          </div>
          <button
            type="button"
            className="qlc-remove-btn"
            aria-label="Remove citation"
            onClick={() => onRemove(index)}
          >
            ✕
          </button>
        </div>
      ))}
    </div>
  );
}

export default function CustomAnswerModal({
  queryId,
  createOpen = false,
  createInEvalSet = false,
  onClose,
  onChanged,
}: CustomAnswerModalProps) {
  const [detail, setDetail] = useState<QueryLogDetailResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [questionText, setQuestionText] = useState('');
  const [answerText, setAnswerText] = useState('');
  const [citations, setCitations] = useState<CustomAnswerCitation[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isConfirmingRemove, setIsConfirmingRemove] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  // Collapses the answer/citations pane to a slim rail so the document pane
  // gets nearly the full width for reading/selecting. Local to the modal,
  // defaults expanded, and resets on every (re)open below.
  const [isRightCollapsed, setIsRightCollapsed] = useState(false);
  const loadGenerationRef = useRef(0);

  // createOpen alone selects create mode; edit mode is any open with a queryId.
  const isCreateMode = createOpen;
  const isOpen = queryId !== null || createOpen;
  const isBusy = isSaving || isDeleting;

  const closeUnlessBusy = useCallback(() => {
    if (!isSaving && !isDeleting) onClose();
  }, [isSaving, isDeleting, onClose]);

  const backdropHandlers = useModalBackdropClose(closeUnlessBusy);
  useEscapeClose(closeUnlessBusy, isOpen);

  // Resets the workbench and (for edit mode) loads the log. createOpen is a
  // dependency so reopening create mode after a close clears the stale draft;
  // in create mode queryId is null, so no detail is ever fetched.
  useEffect(() => {
    const generation = ++loadGenerationRef.current;
    setDetail(null);
    setLoadError(null);
    setActionError(null);
    setIsConfirmingRemove(false);
    setIsRightCollapsed(false);
    setQuestionText('');
    setAnswerText('');
    setCitations([]);
    if (!queryId) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    fetchQueryLog(queryId)
      .then((response) => {
        if (generation !== loadGenerationRef.current) return;
        setDetail(response);
        // Pre-fill the answer box with the curated custom answer if one exists,
        // otherwise the chatbot's own served answer — so an eval-flagged log opens
        // ready to review rather than blank. Saving it creates a custom answer
        // (which is then also served as an override — the intended "curate" step).
        setAnswerText(response.log.customAnswer?.answer ?? response.log.chatbotAnswer ?? '');
        setCitations(response.log.customAnswer?.citations ?? []);
        setIsLoading(false);
      })
      .catch((error: unknown) => {
        if (generation !== loadGenerationRef.current) return;
        setLoadError(
          error instanceof QueryLogApiError ? error.message : 'Failed to load the query.',
        );
        setIsLoading(false);
      });
    return () => {
      loadGenerationRef.current++;
    };
  }, [queryId, createOpen, reloadKey]);

  // A save/create/remove failure surfaces its message inside the answer pane.
  // If that pane is collapsed to the rail, expand it so the error is visible.
  useEffect(() => {
    if (actionError) setIsRightCollapsed(false);
  }, [actionError]);

  const handleAddCitation = useCallback((citation: CustomAnswerCitation) => {
    setCitations((previous) =>
      isDuplicateCitation(previous, citation) ? previous : [...previous, citation],
    );
  }, []);

  const handleRemoveCitation = useCallback((index: number) => {
    setCitations((previous) => previous.filter((_, position) => position !== index));
  }, []);

  const handleSave = useCallback(async () => {
    const trimmedAnswer = answerText.trim();
    if (!queryId || !trimmedAnswer || isSaving || isDeleting) return;
    setIsSaving(true);
    setActionError(null);
    try {
      await saveCustomAnswer(queryId, trimmedAnswer, citations);
      onChanged();
      onClose();
    } catch (error) {
      setActionError(
        error instanceof QueryLogApiError ? error.message : 'Failed to save the custom answer.',
      );
    } finally {
      setIsSaving(false);
    }
  }, [queryId, answerText, citations, isSaving, isDeleting, onChanged, onClose]);

  // Create mode: POST a brand-new admin-authored Q&A. Both fields are required
  // (Save is disabled otherwise); errors surface in the same action-error area.
  const handleCreate = useCallback(async () => {
    const trimmedQuestion = questionText.trim();
    const trimmedAnswer = answerText.trim();
    if (!trimmedQuestion || !trimmedAnswer || isSaving || isDeleting) return;
    setIsSaving(true);
    setActionError(null);
    try {
      await createCustomQueryLog(trimmedQuestion, trimmedAnswer, citations, createInEvalSet);
      onChanged();
      onClose();
    } catch (error) {
      setActionError(
        error instanceof QueryLogApiError ? error.message : 'Failed to create the answer.',
      );
    } finally {
      setIsSaving(false);
    }
  }, [questionText, answerText, citations, createInEvalSet, isSaving, isDeleting, onChanged, onClose]);

  const handleDelete = useCallback(async () => {
    if (!queryId || isSaving || isDeleting) return;
    setIsDeleting(true);
    setActionError(null);
    try {
      await deleteCustomAnswer(queryId);
      onChanged();
      onClose();
    } catch (error) {
      setActionError(
        error instanceof QueryLogApiError ? error.message : 'Failed to remove the custom answer.',
      );
    } finally {
      setIsDeleting(false);
    }
  }, [queryId, isSaving, isDeleting, onChanged, onClose]);

  if (!isOpen) return null;

  const existingAnswer = detail?.log.customAnswer;
  // Create mode also requires a non-empty question; edit mode only the answer.
  const isSaveDisabled = isCreateMode
    ? isBusy || questionText.trim() === '' || answerText.trim() === ''
    : isBusy || answerText.trim() === '';
  // Create mode has no detail to wait on; edit mode shows the workbench only
  // once the log has loaded without error.
  const showWorkbench = isCreateMode || (!isLoading && !loadError && detail !== null);

  return (
    <div className="smrtdocs-modal-overlay" {...backdropHandlers}>
      <div
        className={`smrtdocs-modal qlc-modal${isCreateMode ? ' qlc-modal-create' : ''}`}
        style={{ width: '98vw', height: '94dvh', display: 'flex', flexDirection: 'column' }}
      >
        <div className="smrtdocs-modal-header">
          <h2 style={{ margin: 0, fontSize: '1rem' }}>
            {isCreateMode ? 'New Custom Answer' : 'Custom Answer'}
          </h2>
          <button
            type="button"
            className="qlc-close-btn"
            aria-label="Close"
            disabled={isBusy}
            onClick={closeUnlessBusy}
          >
            ✕
          </button>
        </div>
        <div
          className="smrtdocs-modal-body qlc-body"
          style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
        >
          {!isCreateMode && isLoading && (
            <div className="qlc-center-status">
              <span className="qlc-spinner" />
              Loading query…
            </div>
          )}
          {!isCreateMode && !isLoading && loadError && (
            <div className="qlc-center-status">
              <div className="qlc-error-banner">{loadError}</div>
              <button
                type="button"
                className="btn-cancel"
                onClick={() => setReloadKey((key) => key + 1)}
              >
                Retry
              </button>
            </div>
          )}
          {showWorkbench && (
            <>
              {/* Header: an editable question in create mode, the read-only
                  ask strip in edit mode. */}
              {isCreateMode ? (
                <div className="qlc-topstrip">
                  <div className="qlc-micro-label">Question</div>
                  <textarea
                    className="qlc-input qlc-question-input"
                    value={questionText}
                    onChange={(event) => setQuestionText(event.target.value)}
                    placeholder="Enter the question this answer should serve…"
                    disabled={isBusy}
                    aria-label="Question"
                  />
                </div>
              ) : (
                detail && <TopStrip log={detail.log} />
              )}
              <div className="qlc-split">
                {/* Create mode has no query trace, so no retrieved chunks — the
                    corpus browser's Retrieved-chunks tab is hidden via CSS
                    (qlc-modal-create) and gets an empty chunk set here. */}
                <CorpusBrowser
                  chunks={isCreateMode ? [] : detail?.log.chunks ?? []}
                  chunkTexts={isCreateMode ? {} : detail?.chunkTexts ?? {}}
                  citations={citations}
                  onAddCitation={handleAddCitation}
                  refitToken={isRightCollapsed ? 1 : 0}
                />
                <section
                  className={`qlc-pane qlc-pane-right${isRightCollapsed ? ' qlc-pane-right-collapsed' : ''}`}
                >
                  {/* Collapsed rail — expand toggle, a citations hint, and a
                      reachable Save so the answer can be committed without
                      restoring the split. Hidden (CSS) while expanded. */}
                  <div className="qlc-rail">
                    <button
                      type="button"
                      className="qlc-rail-toggle"
                      onClick={() => setIsRightCollapsed(false)}
                      aria-label="Expand answer panel"
                      title="Expand answer panel"
                    >
                      «
                    </button>
                    <div className="qlc-rail-hint">Citations: {citations.length}</div>
                    <button
                      type="button"
                      className="btn-save qlc-rail-save"
                      disabled={isSaveDisabled}
                      onClick={isCreateMode ? handleCreate : handleSave}
                      aria-label={isCreateMode ? 'Create answer' : 'Save answer'}
                      title={isCreateMode ? 'Create answer' : 'Save answer'}
                    >
                      {isSaving ? <span className="qlc-spinner" /> : isCreateMode ? 'Create' : 'Save'}
                    </button>
                  </div>
                  {/* Answer editor — every child below is hidden (CSS) while
                      the pane is collapsed to the rail above. */}
                  <div className="qlc-pane-right-head">
                    <div className="qlc-micro-label">Admin answer</div>
                    <button
                      type="button"
                      className="qlc-collapse-btn"
                      onClick={() => setIsRightCollapsed(true)}
                      aria-label="Collapse answer panel"
                      title="Collapse answer panel"
                    >
                      »
                    </button>
                  </div>
                  <p className="qlc-hint">
                    Served verbatim to near-duplicate questions (similarity ≥ 0.9).
                  </p>
                  <textarea
                    className="qlc-input qlc-answer-input"
                    value={answerText}
                    onChange={(event) => setAnswerText(event.target.value)}
                    placeholder="Write the answer this question should receive…"
                    disabled={isBusy}
                  />
                  <div className="qlc-citations">
                    <div className="qlc-micro-label">Citations ({citations.length})</div>
                    <CitationList citations={citations} onRemove={handleRemoveCitation} />
                  </div>
                  {existingAnswer && (
                    <p className="qlc-answer-audit">
                      by {existingAnswer.answeredBy} · {formatDateTime(existingAnswer.answeredAt)}
                      {existingAnswer.updatedBy &&
                        ` · updated by ${existingAnswer.updatedBy} · ${formatDateTime(existingAnswer.updatedAt)}`}
                    </p>
                  )}
                  {actionError && <div className="qlc-error-banner qlc-action-error">{actionError}</div>}
                  <div className="qlc-footer">
                    <div className="qlc-footer-side">
                      {existingAnswer && !isConfirmingRemove && (
                        <button
                          type="button"
                          className="qlc-danger-btn"
                          disabled={isBusy}
                          onClick={() => setIsConfirmingRemove(true)}
                        >
                          Remove custom answer
                        </button>
                      )}
                      {existingAnswer && isConfirmingRemove && (
                        <span className="qlc-confirm-remove">
                          Confirm remove?
                          <button
                            type="button"
                            className="qlc-danger-btn"
                            disabled={isBusy}
                            onClick={handleDelete}
                          >
                            {isDeleting && <span className="qlc-spinner" />}
                            Yes
                          </button>
                          <button
                            type="button"
                            className="btn-cancel"
                            disabled={isBusy}
                            onClick={() => setIsConfirmingRemove(false)}
                          >
                            Keep
                          </button>
                        </span>
                      )}
                    </div>
                    <div className="qlc-footer-side">
                      <button
                        type="button"
                        className="btn-cancel"
                        disabled={isBusy}
                        onClick={closeUnlessBusy}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="btn-save"
                        disabled={isSaveDisabled}
                        onClick={isCreateMode ? handleCreate : handleSave}
                      >
                        {isSaving && <span className="qlc-spinner" />}
                        {isCreateMode ? 'Create' : 'Save'}
                      </button>
                    </div>
                  </div>
                </section>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
